import React, { useState, useEffect } from 'react';
import { usePalettes } from '@/hooks/usePalettes';
import { Button } from '@/components/ui/button';
import { Trash2, Edit, PlusCircle, Loader2 } from 'lucide-react';
import { ColorPalette, CreateColorPalette, UpdateColorPalette } from '@shared/schemas/colorPalette';
import { ColorPaletteWithComputedFields } from '@/store/colorPalettes';
import CustomPaletteCreator from '../ui/CustomPaletteCreator';
import { auth } from '@/config/firebase';
import type { ChartPreferences } from '@shared/schemas/chartPreferences';
import { useToast } from '@/hooks/use-toast';

interface PaletteManagementUIProps {
  userId: string;
  organizationId: string;
  currentUserChartPreferences: ChartPreferences | null | undefined;
  onUserDefaultPaletteChange: (paletteId: string) => Promise<void>;
}

export const PaletteManagementUI: React.FC<PaletteManagementUIProps> = ({ 
  userId,
  organizationId,
  currentUserChartPreferences,
  onUserDefaultPaletteChange 
}) => {
  const { allPalettes, fetchPalettes, isLoading, error, deletePalette } = usePalettes();
  const [isCreatorOpen, setIsCreatorOpen] = useState(false);
  const [editingPalette, setEditingPalette] = useState<ColorPaletteWithComputedFields | null>(null);
  const [isSubmittingDelete, setIsSubmittingDelete] = useState(false);
  const [selectedUserDefaultPaletteId, setSelectedUserDefaultPaletteId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (currentUserChartPreferences?.displayPreferences?.defaultColorPalette) {
      setSelectedUserDefaultPaletteId(currentUserChartPreferences.displayPreferences.defaultColorPalette);
    } else if (currentUserChartPreferences) { // Preferences object exists but no default palette ID set
      // If custom palettes are already loaded and there are none, set FireForge as default
      // This check needs to be robust; allPalettes might not be populated when this effect first runs
      // Deferring more complex initial default logic until Profile.tsx can handle it more reliably
      // For now, if no default is set in prefs, user must click to set one.
      // Consider: if (customUserPalettes.length === 0 && systemPalettes.find(p => p.id === 'fireforge')) {
      //   setSelectedUserDefaultPaletteId('fireforge');
      //   onUserDefaultPaletteChange('fireforge');
      // }
    }
  }, [currentUserChartPreferences, allPalettes]); // Added allPalettes to dependencies for potential future logic

  // Separate palettes
  const customUserPalettes = allPalettes.filter(p => p.ownerId !== 'system');
  const systemPalettes = allPalettes.filter(p => p.ownerId === 'system');

  const handleOpenCreator = (palette?: ColorPaletteWithComputedFields) => {
    setEditingPalette(palette || null);
    setIsCreatorOpen(true);
  };

  const handleSetUserDefaultPalette = async (paletteId: string) => {
    setSelectedUserDefaultPaletteId(paletteId);
    try {
      await onUserDefaultPaletteChange(paletteId);
      const palette = allPalettes.find(p => p.id === paletteId);
      toast({
        title: "Default Palette Updated",
        description: `Palette "${palette?.name || paletteId}" is now your default.`,
      });
    } catch (err) {
      console.error("Failed to set default palette:", err);
      toast({
        title: "Update Failed",
        description: "Could not set the default palette. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCloseCreator = () => {
    setIsCreatorOpen(false);
    setEditingPalette(null);
  };

  const handlePaletteSaved = async () => {
    await fetchPalettes(true);
    handleCloseCreator();
  };

  const handleDeletePalette = async (paletteId: string) => {
    const paletteToDelete = customUserPalettes.find(p => p.id === paletteId);
    const paletteName = paletteToDelete?.name || paletteId;

    if (window.confirm(`Are you sure you want to delete the palette "${paletteName}"?`)) {
      setIsSubmittingDelete(true);
      try {
        await deletePalette(paletteId);
        fetchPalettes(true); // Refresh list
        toast({
          title: "Palette Deleted",
          description: `Palette "${paletteName}" was successfully deleted.`,
        });
        // If the deleted palette was the selected default, clear the selection
        if (selectedUserDefaultPaletteId === paletteId) {
          setSelectedUserDefaultPaletteId(null);
          // Note: onUserDefaultPaletteChange is not called here to clear it from backend,
          // as user might want to select another one. Or, we could set it to 'fireforge' default.
        }
      } catch (err) {
        console.error('Failed to delete palette:', err);
        toast({
          title: "Delete Failed",
          description: `Could not delete palette "${paletteName}". Please try again.`,
          variant: "destructive",
        });
      } finally {
        setIsSubmittingDelete(false);
      }
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-4"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading palettes...</div>;
  }

  if (error) {
    return (
      <div className="p-4 text-destructive">
        <p>Error loading palettes: {error}</p>
        <Button variant="link" onClick={() => fetchPalettes(true)} className="p-0 h-auto text-destructive hover:text-destructive-foreground">
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex-grow">
          <h2 className="text-2xl font-semibold">Color Palettes</h2>
          <p className="text-sm text-muted-foreground">Click on a palette to select it as your default.</p>
        </div>
        <Button onClick={() => handleOpenCreator()} size="sm" className="ml-4 flex-shrink-0">
          <PlusCircle className="h-4 w-4 mr-2" /> Add New Palette
        </Button>
      </div>

      {/* Custom Palettes Section */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-muted-foreground pt-4 border-t">Your Custom Palettes</h3>
        {customUserPalettes.length === 0 && !isCreatorOpen && (
          <p className="text-muted-foreground text-sm py-4 text-center border-2 border-dashed rounded-lg">
            You haven't created any custom palettes yet. Click 'Add New Palette' above to create one.
          </p>
        )}
        {/* Grid for custom palettes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {customUserPalettes.map(palette => {
            // const isSystemPalette = false; // Already filtered
            const isSelectedAsUserDefault = palette.id === selectedUserDefaultPaletteId;
            return (
              <div 
                key={palette.id} 
                className={`p-4 border rounded-lg bg-card flex flex-col justify-between cursor-pointer transition-all ${isSelectedAsUserDefault ? 'border-primary bg-primary/10 ring-2 ring-primary/20' : 'hover:shadow-md'}`}
                onClick={() => handleSetUserDefaultPalette(palette.id)}
              >
                {/* Top section: Name, Description, Colors */}
                <div>
                  <h4 className="font-semibold">{palette.name}</h4>
                  <p className="text-sm text-muted-foreground truncate max-w-xs" title={palette.description}>{palette.description || 'No description'}</p>
                  <div className="flex flex-wrap gap-1 my-2"> {/* Display all colors, up to a limit if necessary */}
                    {palette.colors.slice(0, 10).map((color, index) => ( // Show up to 10 colors
                      <div key={index} className="w-4 h-4 rounded-sm border" style={{ backgroundColor: color }} title={color} />
                    ))}
                    {/* Removed +more indicator for now, assuming 10 fits */}
                  </div>
                </div>
                {/* Bottom section: Default color and Action buttons */}
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-dashed"> {/* Border-t for separation */}
                  <p className="text-xs">
                    Default: <span className="inline-block w-4 h-4 rounded-sm border align-middle" style={{backgroundColor: palette.computedDefaultColor}} /> 
                    <span className="align-middle"> {palette.computedDefaultColor}</span>
                    {palette.isGradient && <span className="ml-2 text-muted-foreground align-middle">(Gradient)</span>}
                  </p>
                  {/* Management buttons for custom palettes - aligned right */}
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenCreator(palette)} title="Edit Palette" disabled={isSubmittingDelete} className="h-6 w-6 p-0">
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeletePalette(palette.id)} title="Delete Palette" disabled={isSubmittingDelete} className="h-6 w-6 p-0">
                      {isSubmittingDelete && editingPalette?.id === palette.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 text-destructive" />}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* System Palettes Section */}
      {systemPalettes.length > 0 && (
        <div className="space-y-4 pt-6 mt-6 border-t">
          <h3 className="text-xl font-semibold text-muted-foreground">System Default Palettes</h3>
          {/* Grid for system palettes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {systemPalettes.map(palette => {
              const isSelectedAsUserDefault = palette.id === selectedUserDefaultPaletteId;
              return (
                <div 
                  key={palette.id} 
                  className={`p-4 border rounded-lg bg-card flex flex-col justify-between opacity-75 cursor-pointer transition-all ${isSelectedAsUserDefault ? 'border-primary bg-primary/10 ring-2 ring-primary/20' : 'hover:shadow-md'}`}
                  onClick={() => handleSetUserDefaultPalette(palette.id)}
                >
                  <div>
                    <h4 className="font-semibold">{palette.name} <span className="text-xs text-muted-foreground">(System)</span></h4>
                    <p className="text-sm text-muted-foreground truncate max-w-xs" title={palette.description}>{palette.description || 'No description'}</p>
                    <div className="flex flex-wrap gap-1 my-2"> {/* Display all colors */}
                      {palette.colors.slice(0, 10).map((color, index) => ( // Show up to 10 colors
                        <div key={index} className="w-4 h-4 rounded-sm border" style={{ backgroundColor: color }} title={color} />
                      ))}
                      {/* Removed +more indicator */}
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-dashed"> {/* Border-t */}
                    <p className="text-xs">
                      Default: <span className="inline-block w-4 h-4 rounded-sm border align-middle" style={{backgroundColor: palette.computedDefaultColor}} /> 
                      <span className="align-middle"> {palette.computedDefaultColor}</span>
                      {palette.isGradient && <span className="ml-2 text-muted-foreground align-middle">(Gradient)</span>}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Fallback if allPalettes is empty (e.g., during initial load error or truly no palettes) */}
      {allPalettes.length === 0 && !isCreatorOpen && !isLoading && !error && (
          <p className="text-muted-foreground text-sm py-4 text-center border-2 border-dashed rounded-lg">
            No palettes found. System palettes may be unavailable or you haven't created any custom palettes yet.
          </p>
      )}

      <CustomPaletteCreator 
        isOpen={isCreatorOpen} 
        onClose={handleCloseCreator} 
        onSave={handlePaletteSaved} 
        editingPalette={editingPalette} 
        userId={userId}
        organizationId={organizationId}
      />
    </div>
  );
}; 